import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from 'urql';
import PageHeader from '../../../components/PageHeader.jsx';
import StickyActions from '../../../components/StickyActions.jsx';
import { CERRAR_INVENTARIO, GET_INVENTARIO, INVENTARIO_DIFERENCIAS, REGISTRAR_CONTEO } from './gql.js';

function formatNumber(value, fractionDigits = 3) {
  return Number(value ?? 0).toLocaleString('es-AR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });
}

export default function InventarioDetail({ inventarioId, onBack }) {
  const [{ data, fetching, error }, reexecute] = useQuery({
    query: GET_INVENTARIO,
    variables: { id: inventarioId },
    pause: !inventarioId,
  });
  const [{ data: diferenciasData }, reexecuteDiferencias] = useQuery({
    query: INVENTARIO_DIFERENCIAS,
    variables: { id: inventarioId },
    pause: !inventarioId,
  });
  const [, registrarConteo] = useMutation(REGISTRAR_CONTEO);
  const [, cerrarInventario] = useMutation(CERRAR_INVENTARIO);
  const [conteos, setConteos] = useState({});
  const [statusMessage, setStatusMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [closing, setClosing] = useState(false);

  const inventario = data?.getInventario;
  const diferencias = diferenciasData?.inventarioDiferencias ?? [];

  useEffect(() => {
    if (inventario?.items?.length) {
      setConteos((prev) => {
        const next = { ...prev };
        inventario.items.forEach((item) => {
          next[item.productoId] =
            prev[item.productoId] != null
              ? prev[item.productoId]
              : item.stockFisico != null
              ? Number(item.stockFisico)
              : Number(item.stockSistema);
        });
        return next;
      });
    }
  }, [inventario]);

  const items = useMemo(() => inventario?.items ?? [], [inventario]);

  const handleConteoChange = (productoId, value) => {
    setConteos((prev) => ({
      ...prev,
      [productoId]: value === '' ? '' : Number(value),
    }));
  };

  const handleRegistrarConteos = async () => {
    if (!inventario) return;
    setSaving(true);
    setStatusMessage('');
    try {
      const payload = items.map((item) => ({
        productoId: String(item.productoId),
        stockFisico:
          conteos[item.productoId] === '' || conteos[item.productoId] == null
            ? Number(item.stockFisico ?? item.stockSistema)
            : Number(conteos[item.productoId]),
        observacion: item.observacion || null,
      }));
      await registrarConteo({
        input: {
          inventarioId: String(inventarioId),
          items: payload,
        },
      });
      setStatusMessage('Conteos guardados correctamente.');
      reexecute({ requestPolicy: 'network-only' });
      reexecuteDiferencias({ requestPolicy: 'network-only' });
    } catch (mutationError) {
      setStatusMessage(mutationError.message);
    } finally {
      setSaving(false);
    }
  };

  const handleCerrar = async () => {
    if (!inventario || inventario.estado === 'CERRADO') {
      onBack?.();
      return;
    }
    setClosing(true);
    setStatusMessage('');
    try {
      await cerrarInventario({ id: String(inventarioId) });
      setStatusMessage('Inventario cerrado correctamente.');
      reexecute({ requestPolicy: 'network-only' });
      reexecuteDiferencias({ requestPolicy: 'network-only' });
    } catch (mutationError) {
      setStatusMessage(mutationError.message);
    } finally {
      setClosing(false);
    }
  };

  const resumen = useMemo(() => {
    const positivos = diferencias.filter((item) => Number(item.diferencia) > 0);
    const negativos = diferencias.filter((item) => Number(item.diferencia) < 0);
    return {
      positivos,
      negativos,
      totalPositivo: positivos.reduce((acc, item) => acc + Number(item.diferencia ?? 0), 0),
      totalNegativo: negativos.reduce((acc, item) => acc + Number(item.diferencia ?? 0), 0),
    };
  }, [diferencias]);

  return (
    <div className="mx-auto flex w-full max-w-[1400px] flex-col gap-6 px-6 py-8">
      <PageHeader
        title={`Inventario #${inventario?.id ?? ''}`.trim()}
        subtitle="Seguimiento de conteos físicos y ajustes generados."
      />

      <section className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel),0.7)] p-6 shadow-sm">
        {fetching ? (
          <p className="text-sm text-muted">Cargando inventario…</p>
        ) : error ? (
          <p className="text-sm text-red-300">{error.message}</p>
        ) : inventario ? (
          <div className="space-y-6">
            <dl className="grid grid-cols-12 gap-4 text-sm text-[rgb(var(--fg))]">
              <div className="col-span-12 md:col-span-3">
                <dt className="font-medium text-muted">Sucursal</dt>
                <dd className="mt-1">{inventario.sucursalNombre || `#${inventario.sucursalId}`}</dd>
              </div>
              <div className="col-span-12 md:col-span-3">
                <dt className="font-medium text-muted">Inicio</dt>
                <dd className="mt-1">
                  {inventario.fechaInicio ? new Date(inventario.fechaInicio).toLocaleString() : '—'}
                </dd>
              </div>
              <div className="col-span-12 md:col-span-3">
                <dt className="font-medium text-muted">Cierre</dt>
                <dd className="mt-1">
                  {inventario.fechaCierre ? new Date(inventario.fechaCierre).toLocaleString() : '—'}
                </dd>
              </div>
              <div className="col-span-12 md:col-span-3">
                <dt className="font-medium text-muted">Estado</dt>
                <dd className="mt-1">{inventario.estado}</dd>
              </div>
              <div className="col-span-12">
                <dt className="font-medium text-muted">Observación</dt>
                <dd className="mt-1 whitespace-pre-line">{inventario.observacion || '—'}</dd>
              </div>
            </dl>

            {statusMessage ? (
              <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.55)] px-4 py-3 text-sm text-[rgb(var(--fg))]">
                {statusMessage}
              </div>
            ) : null}

            <div className="overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.6)]">
              <table className="w-full text-left text-sm">
                <thead className="bg-[rgba(var(--subpanel),0.35)] text-xs uppercase tracking-wide text-muted">
                  <tr>
                    <th className="px-4 py-3">Producto</th>
                    <th className="px-4 py-3 text-right">Stock sistema</th>
                    <th className="px-4 py-3 text-right">Stock físico</th>
                    <th className="px-4 py-3 text-right">Diferencia</th>
                    <th className="px-4 py-3">Observación</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length ? (
                    items.map((item) => {
                      const fisico = conteos[item.productoId];
                      const diferencia =
                        (fisico === '' || fisico == null ? Number(item.stockFisico ?? item.stockSistema) : Number(fisico)) -
                        Number(item.stockSistema ?? 0);
                      return (
                        <tr key={item.id} className="border-t border-[rgb(var(--border))]/60">
                          <td className="px-4 py-3">
                            <div className="font-medium text-[rgb(var(--fg))]">{item.productoDescripcion || `#${item.productoId}`}</div>
                            <div className="text-xs text-muted">Código: {item.productoCodigo || '—'}</div>
                          </td>
                          <td className="px-4 py-3 text-right">{formatNumber(item.stockSistema)}</td>
                          <td className="px-4 py-3 text-right">
                            {inventario.estado === 'CERRADO' ? (
                              <span className="font-semibold">{formatNumber(item.stockFisico ?? fisico)}</span>
                            ) : (
                              <input
                                type="number"
                                min="0"
                                step="0.001"
                                value={fisico ?? ''}
                                onChange={(event) => handleConteoChange(item.productoId, event.target.value)}
                                className="w-full rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.85)] px-3 py-2 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
                              />
                            )}
                          </td>
                          <td className={`px-4 py-3 text-right ${diferencia > 0 ? 'text-emerald-300' : diferencia < 0 ? 'text-red-300' : 'text-[rgb(var(--fg))]'}`}>
                            {formatNumber(diferencia)}
                          </td>
                          <td className="px-4 py-3 text-xs text-muted">{item.observacion || '—'}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={5} className="px-4 py-6 text-center text-sm text-muted">
                        No hay ítems asociados al inventario.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--bg),0.55)] p-4 text-sm text-[rgb(var(--fg))]">
              <h3 className="text-base font-semibold text-[rgb(var(--fg))]">Diferencias destacadas</h3>
              {diferencias.length ? (
                <div className="mt-3 grid grid-cols-12 gap-4">
                  <div className="col-span-12 md:col-span-6">
                    <h4 className="text-sm font-medium text-emerald-300">Sobrantes ({resumen.positivos.length})</h4>
                    <ul className="mt-2 space-y-1 text-xs text-muted">
                      {resumen.positivos.map((item) => (
                        <li key={`pos-${item.productoId}`}>
                          {item.productoDescripcion || `#${item.productoId}`} · +{formatNumber(item.diferencia)}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="col-span-12 md:col-span-6">
                    <h4 className="text-sm font-medium text-red-300">Faltantes ({resumen.negativos.length})</h4>
                    <ul className="mt-2 space-y-1 text-xs text-muted">
                      {resumen.negativos.map((item) => (
                        <li key={`neg-${item.productoId}`}>
                          {item.productoDescripcion || `#${item.productoId}`} · {formatNumber(item.diferencia)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <p className="mt-3 text-sm text-muted">Aún no hay diferencias registradas.</p>
              )}
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted">No encontramos el inventario solicitado.</p>
        )}
      </section>

      <StickyActions
        left={
          <button
            type="button"
            onClick={onBack}
            className="rounded-xl border border-[rgb(var(--border))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--fg))] transition hover:bg-[rgba(var(--fg),0.05)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))]"
          >
            Volver
          </button>
        }
        right={
          inventario && inventario.estado === 'ABIERTO' ? (
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleRegistrarConteos}
                disabled={saving}
                className="rounded-xl border border-[rgb(var(--brand))] bg-transparent px-4 py-2 text-sm font-medium text-[rgb(var(--brand))] transition hover:bg-[rgba(var(--brand),0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Guardando…' : 'Registrar conteos'}
              </button>
              <button
                type="button"
                onClick={handleCerrar}
                disabled={closing}
                className="rounded-xl bg-[rgb(var(--brand))] px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-[rgba(var(--brand-700),0.9)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--ring))] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {closing ? 'Cerrando…' : 'Cerrar inventario'}
              </button>
            </div>
          ) : (
            <p className="text-sm text-muted">Este inventario ya fue cerrado. Los ajustes están registrados en el Kardex.</p>
          )
        }
      />
    </div>
  );
}
